import { useState, useEffect } from "react";
// aos
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaGift,
  FaCheck,
  FaHeadset,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaBuilding,
  FaVideo,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarCheck,
} from "react-icons/fa";
import Layout from "../Layout/Layout";
import { useDispatch } from "react-redux";
import { sendMessage } from "../../states/actions/appointmentAction";
import { ToastContainer } from "react-toastify";

// ✅ OUTSIDE main component
function Input({ label, placeholder, name, value, onChange, error }) {
  return (
    <div>
      <label className="block mb-2 text-sm text-gray-600">{label}</label>

      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full px-5 py-3 bg-white border rounded-lg focus:outline-none focus:ring
        ${
          error
            ? "border-red-500 focus:ring-red-400"
            : "border-gray-200 focus:ring-[#C9A227]"
        }`}
      />

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function Step1({ formData, handleChange, errors }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-2">Personal Information</h2>

      <div className="grid md:grid-cols-2 gap-6">
        <Input
          label="Full Name *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          placeholder="Enter your full name"
        />

        <Input
          label="Email Address *"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          placeholder="Enter your email"
        />

        <Input
          label="Phone Number *"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          error={errors.phone}
          placeholder="+251 9XX XXX XXX"
        />

        <Input
          label="Company Name (Optional)"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="Your company name"
        />
      </div>
    </div>
  );
}

function Step4({ formData, handleChange, selectedTime }) {
  return (
    <div>
      <div className="mb-8">
        <h3 className="text-2xl font-semibold mb-2">
          Confirm Your Appointment
        </h3>
        <p className="text-gray-500">
          Please review your booking details and add any additional information.
        </p>
      </div>

      {/* SUMMARY CARD */}
      <div className="border rounded-xl p-6 bg-gray-50 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <FaCalendarCheck className="text-[#C9A227]" />
          <h4 className="font-semibold">Booking Summary</h4>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Name</span>
            <span className="font-medium">{formData.name}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Email</span>
            <span className="font-medium">{formData.email}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Phone</span>
            <span className="font-medium">{formData.phone}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Consultation Type</span>
            <span className="font-medium">{formData.consultationType}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Practice Area</span>
            <span className="font-medium">{formData.practiceArea}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Date</span>
            <span className="font-semibold text-[#C9A227]">
              {formData.date}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Time</span>
            <span className="font-semibold text-[#C9A227]">{selectedTime}</span>
          </div>
        </div>
      </div>

      {/* MESSAGE */}
      <div>
        <label className="block mb-2 text-sm text-gray-600">
          Additional Information (Optional)
        </label>

        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us briefly about your legal matter..."
          className="w-full p-4 border rounded-lg bg-gray-100 focus:outline-none focus:ring focus:ring-[#C9A227]"
        />
      </div>
    </div>
  );
}
// ✅ Main component

const AppointmentPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 3000 });
  }, []);

  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    consultationType: "In-Person",
    practiceArea: "",
    date: "",
    time: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    const appointmentData = {
      ...formData,
      date: formData.date,
      time: selectedTime,
    };

    // console.log("appointment sent is:", appointmentData);

    dispatch(sendMessage(appointmentData));
  };

  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(16);
  const [selectedTime, setSelectedTime] = useState("02:00 PM");
  const [errors, setErrors] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());

  const next = () => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = "Name is required";
      if (!formData.email.trim()) newErrors.email = "Email is required";
      if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    }

    if (step === 2) {
      if (!formData.practiceArea)
        newErrors.practiceArea = "Please select a practice area";
    }

    if (step === 3) {
      if (!formData.date) newErrors.date = "Please select a date";
      if (!formData.time) newErrors.time = "Please select a time";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setStep((s) => Math.min(s + 1, 4));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const dates = [];

    for (let i = 0; i < firstDay; i++) {
      dates.push(null);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      dates.push(d);
    }

    return dates;
  };

  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const monthName = currentDate.toLocaleString("default", { month: "long" });
  const year = currentDate.getFullYear();

  const gold = "#C9A227";

  const Sidebar = () => (
    <div className="w-full lg:w-[300px] bg-gray-50 p-6 md:p-8 border-r">
      <div className="mb-8">
        <h3 className="flex items-center gap-2 font-semibold mb-4">
          <FaGift /> What You Get
        </h3>
        <ul className="space-y-3 text-sm text-gray-600">
          {[
            "Free 30-minute initial consultation",
            "Expert legal assessment of your case",
            "Clear explanation of your options",
            "Transparent fee structure discussion",
            "No obligation to proceed",
          ].map((item, i) => (
            <li key={i} className="flex gap-2">
              <FaCheck className="text-[#C9A227] mt-1" /> {item}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="flex items-center gap-2 font-semibold mb-4">
          <FaHeadset /> Need Help?
        </h3>
        <div className="space-y-4 text-sm text-gray-600">
          <div className="flex items-center gap-3">
            <FaPhoneAlt /> +251 995 98 9898
          </div>
          <div className="flex items-center gap-3">
            <FaEnvelope /> info@aderaw.com
          </div>
          <div className="flex items-center gap-3">
            <FaClock /> Mon-Fri: 8:30 AM - 5:30 PM
          </div>
        </div>
      </div>
    </div>
  );

  const Stepper = () => (
    <div className="flex justify-between mb-10">
      {["Your Details", "Service Type", "Date & Time", "Confirm"].map(
        (label, i) => (
          <div key={i} className="flex flex-col items-center w-full">
            <div
              className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold ${step === i + 1 ? "text-white" : "bg-gray-200"}`}
              style={step === i + 1 ? { backgroundColor: gold } : {}}
            >
              {i + 1}
            </div>
            <span className="text-xs mt-2 text-gray-500">{label}</span>
          </div>
        ),
      )}
    </div>
  );

  const Step2 = () => {
    const Card = ({ value, title, desc, icon }) => (
      <label
        onClick={() =>
          setFormData((prev) => ({
            ...prev,
            consultationType: value,
          }))
        }
        className={`cursor-pointer border rounded-2xl p-6 text-center transition-all ${
          formData.consultationType === value
            ? "border-[#C9A227] bg-[#F8F5ED]"
            : "border-gray-200"
        }`}
      >
        <input
          type="radio"
          name="consultationType"
          value={value}
          checked={formData.consultationType === value}
          readOnly
          className="hidden"
        />

        <div
          className={`text-2xl mb-3 ${
            formData.consultationType === value
              ? "text-[#C9A227]"
              : "text-gray-400"
          }`}
        >
          {icon}
        </div>

        <h4 className="font-semibold text-gray-800">{title}</h4>
        <p className="text-sm text-gray-500 mt-1">{desc}</p>
      </label>
    );

    return (
      <div>
        <h2 className="text-2xl font-semibold mb-2">Select Service</h2>
        <p className="text-gray-500 mb-6">
          Choose your preferred consultation type and practice area.
        </p>

        <label className="block mb-4 text-sm text-gray-600">
          Consultation Type <span className="text-red-500">*</span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <Card
            value="In-Person"
            title="In-Person"
            desc="Visit our office"
            icon={<FaBuilding />}
          />

          <Card
            value="Video Call"
            title="Video Call"
            desc="Zoom / Google Meet"
            icon={<FaVideo />}
          />

          <Card
            value="Phone Call"
            title="Phone Call"
            desc="We'll call you"
            icon={<FaPhoneAlt />}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block mb-2 text-sm text-gray-600">
              Practice Area <span className="text-red-500">*</span>
            </label>

            <select
              name="practiceArea"
              value={formData.practiceArea}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227] focus:ring focus:outline-none"
            >
              <option value="">Select a practice area</option>
              <option>Corporate & Commercial Law</option>
              <option>Litigation & Dispute Resolution</option>
              <option>Banking & Finance</option>
              <option>Real Estate & Property</option>
              <option>Employment & Labor Law</option>
              <option>Investment Law</option>
              <option>Tax Advisory</option>
              <option>Intellectual Property</option>
              <option>Contract Law</option>
              <option>Other</option>
            </select>
          </div>

          <div>
            <label className="block mb-2 text-sm text-gray-600">
              Preferred Attorney (Optional)
            </label>

            <select className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227] focus:ring focus:outline-none">
              <option>No preference</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  const times = [
    "09:00 AM",
    "09:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "02:00 PM",
    "02:30 PM",
    "03:00 PM",
    "03:30 PM",
    "04:00 PM",
    "04:30 PM",
  ];
  const dates = generateCalendar();

  const Step3 = () => (
    <div>
      <div className="mb-8">
        <h3 className="text-2xl font-semibold mb-2">Select Date & Time</h3>
        <p className="text-gray-500">
          Choose your preferred appointment date and time slot.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* DATE PICKER */}
        <div className="border rounded-xl p-6">
          <div className="flex items-center gap-2 text-[#C9A227] font-semibold mb-4">
            <FaCalendarAlt /> Select Date
          </div>

          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">
              {monthName} {year}
            </h4>
            <div className="flex gap-2 text-gray-500">
              <button onClick={prevMonth} className="p-1 hover:text-black">
                <FaChevronLeft />
              </button>

              <button onClick={nextMonth} className="p-1 hover:text-black">
                <FaChevronRight />
              </button>
            </div>
          </div>

          {/* DAYS */}
          <div className="grid grid-cols-7 text-center text-xs text-gray-500 mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>

          {/* DATES */}
          <div className="grid grid-cols-7 gap-2 text-sm">
            {dates.map((date, i) => (
              <div
                key={i}
                onClick={() => {
                  if (!date) return;

                  setSelectedDate(date);

                  const fullDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    date,
                  );

                  setFormData((prev) => ({
                    ...prev,
                    date: fullDate.toLocaleDateString("en-CA"),
                  }));
                }}
                className={`p-2 rounded text-center cursor-pointer transition
      ${
        !date
          ? "invisible"
          : selectedDate === date
            ? "bg-[#C9A227] text-white"
            : "bg-gray-100 hover:bg-gray-200"
      }`}
              >
                {date}
              </div>
            ))}
          </div>
        </div>

        {/* TIME PICKER */}
        <div className="border rounded-xl p-6">
          <div className="flex items-center gap-2 text-[#C9A227] font-semibold mb-4">
            <FaClock /> Select Time
          </div>

          <div className="grid grid-cols-3 gap-3">
            {times.map((t, i) => (
              <div
                key={i}
                onClick={() => {
                  setSelectedTime(t);

                  setFormData((prev) => ({
                    ...prev,
                    time: t,
                  }));
                }}
                className={`p-3 text-center rounded-lg cursor-pointer transition
                ${
                  selectedTime === t
                    ? "bg-[#C9A227] text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout
      title="Appointment Page | Aderaw Addisu Law Office"
      description="Legal services in Bahir Dar including arbitration, litigation, contracts and consultation."
      keywords="Legal services Bahir Dar, Lawyer Ethiopia"
    >
      <section className="h-[45vh] md:h-[55vh] bg-gradient-to-b from-[#1e2f4a] to-[#0b1628] text-white flex items-center justify-center px-6">
        <div className="max-w-5xl w-full text-center">
          {/* Top Badge */}
          <div className="inline-block mb-6">
            <span className="text-[11px] tracking-widest uppercase px-5 py-2 rounded-full border border-yellow-500/40 text-yellow-400">
              Free Initial Consultation
            </span>
          </div>

          {/* Title */}

          <h1 className="text-3xl md:text-6xl font-light mb-6 leading-tight font-serif">
            Book Your
            <span className="text-yellow-400 font-serif font-medium tracking-tight">
              Consultation
            </span>
          </h1>
          {/* Description */}
          <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-12">
            Schedule a meeting with our experienced attorneys. We're here to
            provide expert legal guidance tailored to your needs.
          </p>
        </div>
      </section>

      <div className="flex flex-col lg:flex-row min-h-screen bg-white">
        <Sidebar />

        <div className="flex-1 p-6 md:p-10 max-w-5xl mx-auto">
          <Stepper />

          {step === 1 && (
            <Step1
              formData={formData}
              handleChange={handleChange}
              errors={errors}
            />
          )}
          {step === 2 && <Step2 />}
          {step === 3 && <Step3 />}
          {step === 4 && (
            <Step4
              formData={formData}
              handleChange={handleChange}
              selectedTime={selectedTime}
            />
          )}

          <div className="flex justify-between mt-10 flex-col sm:flex-row gap-4">
            {step > 1 && (
              <button onClick={prev} className="px-6 py-2 border rounded">
                Back
              </button>
            )}
            <button
              onClick={step === 4 ? handleSubmit : next}
              className="px-6 py-3 text-white rounded"
              style={{ backgroundColor: gold }}
            >
              {step === 4 ? "Confirm Booking" : "Continue"}
            </button>
          </div>
        </div>
      </div>
      <ToastContainer />
    </Layout>
  );
};
export default AppointmentPage;
